import type { InsightFamily } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { exclamationTriangle } from "@amzn/storm-ui-icons";
import React, { useMemo, useRef } from "react";
import { isNonNullish } from "remeda";

import {
    InsightFamilyMetadataAttributes,
    InsightFamilyMetadataBrandType,
} from "../../../../hooks/insightFamilies/useInsightFamiliesFiltersAction";
import { useContentCardShown } from "../../../../hooks/misc/useContentCardShown";
import { CardDetailsSource } from "../../../../types/card";
import type { CardDetail } from "../../../../types/card";
import {
    formatFamilyName,
    formatObjectives,
    formatStartEndDate,
    formatUploadDate,
    getPlannedRefreshDate,
    isRefreshRequired,
} from "../../../../utils/insightFamilies.utils";
import { IconWithTooltip } from "../../../common/IconWithTooltip";
import { CardLayout } from "../insightDetails/CardLayout";
import { CardLayoutV2 } from "../insightDetails/CardLayoutV2";
import { InsightRefreshWarning } from "../insightsRefresh/InsightRefreshWarning";

import { InsightFamilyCardAction } from "./InsightFamilyCardAction";

const INSIGHT_FAMILY_TRACKING_ID_MISSING = "INSIGHT_FAMILY_TRACKING_NOT_FOUND";

export interface InsightFamilyCardProps {
    insight: InsightFamily;
    selectedInsightSubtypeIds?: string[];
    index?: number;
    summary?: string;
}

export const InsightFamilyCard: React.FunctionComponent<
    InsightFamilyCardProps
> = ({ insight, selectedInsightSubtypeIds, index, summary }) => {
    const {
        identifier,
        browseNode,
        brand,
        brandId,
        title,
        familyName,
        objectives,
        metadata = {},
        tag,
        insightFamilyTrackingId = INSIGHT_FAMILY_TRACKING_ID_MISSING, // fallback
    } = insight;
    const insightType = InsightType.INSIGHT;
    let datasetDate,
        refreshCadence,
        uploadDate,
        startDate,
        brandType,
        endDate = "";

    if (isNonNullish(metadata)) {
        datasetDate =
            metadata[InsightFamilyMetadataAttributes.DATASET_DATE] ?? "";
        refreshCadence = metadata[
            InsightFamilyMetadataAttributes.UPDATE_CADENCE
        ]
            ? metadata[
                  InsightFamilyMetadataAttributes.UPDATE_CADENCE
              ].toLowerCase()
            : "";
        uploadDate =
            metadata[InsightFamilyMetadataAttributes.UPLOAD_DATE] ?? "";
        startDate = metadata[InsightFamilyMetadataAttributes.START_DATE] ?? "";
        endDate = metadata[InsightFamilyMetadataAttributes.END_DATE] ?? "";
        brandType = metadata[InsightFamilyMetadataAttributes.BRAND_TYPE] ?? "";
    }

    useContentCardShown({ identifier, insightType });
    const details: CardDetail[] = [
        {
            label: "Last updated on",
            value: formatUploadDate(uploadDate),
            renderOn: [CardDetailsSource.CARD],
        },
        {
            label: "From",
            value: formatStartEndDate(startDate, endDate),
            renderOn: [CardDetailsSource.SIDEPANEL],
        },
        {
            label: "Last updated on",
            value: formatUploadDate(uploadDate),
            tertiaryText: `Updated ${refreshCadence}`,
            renderOn: [CardDetailsSource.SIDEPANEL],
        },
        {
            label:
                brandType === InsightFamilyMetadataBrandType.ICM_BRAND
                    ? "Businessline"
                    : "Brand",
            value: brand,
        },
        {
            label: "BN",
            value: browseNode,
            lineClamp: 2,
        },
        {
            label: "Family name",
            value: formatFamilyName(familyName),
        },
        {
            label: "Objective",
            value: formatObjectives(objectives),
            renderOn: [CardDetailsSource.SIDEPANEL],
        },
    ];

    return (
        <CardLayout
            description={title ?? ""}
            details={details}
            actions={
                <InsightFamilyCardAction
                    metadata={
                        metadata as Record<
                            InsightFamilyMetadataAttributes,
                            string
                        >
                    }
                    identifier={identifier!}
                    selectedInsightSubtypeIds={selectedInsightSubtypeIds}
                    insightFamilyTrackingId={insightFamilyTrackingId}
                    details={details}
                    title={title!}
                    index={index}
                    brandId={brandId}
                    familyName={familyName!}
                />
            }
            tag={tag}
            dataTestId={identifier ?? ""}
            key={identifier}
            summary={summary}
        />
    );
};

export const InsightFamilyCardV2: React.FunctionComponent<
    InsightFamilyCardProps
> = ({ insight, selectedInsightSubtypeIds, index }) => {
    const {
        identifier,
        browseNode,
        brand,
        title,
        familyName,
        objectives,
        metadata = {},
        tag,
        insightFamilyTrackingId = INSIGHT_FAMILY_TRACKING_ID_MISSING, // fallback
        brandId,
    } = insight;
    const insightType = InsightType.INSIGHT;
    let datasetDate,
        refreshCadence,
        uploadDate,
        startDate,
        brandType,
        endDate = "";

    if (isNonNullish(metadata)) {
        datasetDate =
            metadata[InsightFamilyMetadataAttributes.DATASET_DATE] ?? "";
        refreshCadence = metadata[
            InsightFamilyMetadataAttributes.UPDATE_CADENCE
        ]
            ? metadata[
                  InsightFamilyMetadataAttributes.UPDATE_CADENCE
              ].toLowerCase()
            : "";
        uploadDate =
            metadata[InsightFamilyMetadataAttributes.UPLOAD_DATE] ?? "";
        startDate = metadata[InsightFamilyMetadataAttributes.START_DATE] ?? "";
        brandType = metadata[InsightFamilyMetadataAttributes.BRAND_TYPE] ?? "";
        endDate = metadata[InsightFamilyMetadataAttributes.END_DATE] ?? "";
    }

    const currentDate = useRef(new Date()).current;
    const requiresRefresh = useMemo(() => {
        return isRefreshRequired(datasetDate, currentDate, refreshCadence);
    }, [currentDate, datasetDate, refreshCadence]);
    const plannedRefreshDate = useMemo(() => {
        return getPlannedRefreshDate(
            datasetDate,
            refreshCadence,
            requiresRefresh,
        );
    }, [datasetDate, refreshCadence, requiresRefresh]);

    useContentCardShown({ identifier, insightType });
    const details: CardDetail[] = [
        {
            label: "Last updated on",
            value: formatUploadDate(uploadDate),
            renderOn: [CardDetailsSource.CARD],
        },
        {
            label: "From",
            value: formatStartEndDate(startDate, endDate),
            renderOn: [CardDetailsSource.SIDEPANEL],
        },
        {
            label: "Last updated on",
            value: formatUploadDate(uploadDate),
            tertiaryText: `Updated ${refreshCadence}`,
            renderOn: [CardDetailsSource.SIDEPANEL],
            additionalElements: [
                {
                    render: (
                        <IconWithTooltip
                            testIdPrefix="refresh-warning"
                            icon={exclamationTriangle}
                            tooltipElement={
                                <InsightRefreshWarning
                                    uploadDate={uploadDate}
                                    plannedRefreshDate={plannedRefreshDate}
                                    insight={insight}
                                />
                            }
                        />
                    ),
                    visible: requiresRefresh,
                },
            ],
        },
        {
            label:
                brandType === InsightFamilyMetadataBrandType.ICM_BRAND
                    ? "Businessline"
                    : "Brand",
            value: brand,
        },
        {
            label: "BN",
            value: browseNode,
            lineClamp: 2,
        },
        {
            label: "Family name",
            value: formatFamilyName(familyName),
        },
        {
            label: "Objective",
            value: formatObjectives(objectives),
            renderOn: [CardDetailsSource.SIDEPANEL],
        },
    ];

    return (
        <CardLayoutV2
            // description={title ?? ""}
            header={formatFamilyName(familyName)}
            title={brand}
            subtitle={browseNode}
            footer={`Last Updated: ${formatUploadDate(uploadDate)}`}
            details={[]}
            actions={
                <InsightFamilyCardAction
                    identifier={identifier!}
                    metadata={
                        metadata as Record<
                            InsightFamilyMetadataAttributes,
                            string
                        >
                    }
                    selectedInsightSubtypeIds={selectedInsightSubtypeIds}
                    insightFamilyTrackingId={insightFamilyTrackingId}
                    details={details}
                    title={title!}
                    index={index}
                    brandId={brandId}
                    familyName={familyName!}
                />
            }
            tag={tag}
            dataTestId="insight-family-card"
            key={identifier}
        />
    );
};
